import { collection, deleteDoc, doc, getDoc, getDocs, query, where, writeBatch } from "firebase/firestore";
import { db } from "../../../firebase";
import { BATCH_WRITE_SIZE, COL_SALES_IMPORT_MONTHS, SALES_IMPORT_LINE_CHUNKS_SUB } from "../constants";
import { normalizeSalesImportBranchId } from "../config/salesImportBranchIds";
import {
  monthsOverlappingRange,
  normalizeTranDateString,
  rowMatchesDateRange,
  weekRangeOverlapsFilter,
} from "../utils/dateParse";
import { buildMonthWeekMeta, salesImportMonthDocId } from "../utils/weekUtils";
import { SALES_WEEKS_PER_MONTH } from "../../../utils/weeklySalesUpload";
import { salesTransactionsWeekCategory } from "./salesLineChunksService";

export function normalizeVoucherType(row) {
  const raw = String(
    row?.voucherType ?? row?.VoucherType ?? row?.vouchertype ?? row?.["Voucher Type"] ?? ""
  )
    .trim()
    .replace(/\s+/g, " ");
  if (!raw) return "UNKNOWN";
  return raw
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

/** Line sale value: prefer Amount column (SQL export line total). */
export function lineSaleAmount(row) {
  const amount = Number(row.amount);
  if (Number.isFinite(amount)) return amount;
  const billAmount = Number(row.billAmount);
  if (Number.isFinite(billAmount)) return billAmount;
  return 0;
}

/** Per-line amount for Advanced Reports KPIs, Brand by Branch, and similar line totals. */
export function advancedReportLineAmount(row) {
  return lineSaleAmount(row);
}

/** Line Amount column only — never falls back to BillAmount (avoids duplicate header totals). */
export function lineAmountOnly(row) {
  const amount = Number(row.amount);
  return Number.isFinite(amount) ? amount : 0;
}

/** Bill header total: prefer BillAmount column. */
export function lineBillAmount(row) {
  const billAmount = Number(row.billAmount);
  if (Number.isFinite(billAmount)) return billAmount;
  const amount = Number(row.amount);
  if (Number.isFinite(amount)) return amount;
  return 0;
}

/** BillAmount column only — never the line Amount column. */
export function lineBillAmountHeaderOnly(row) {
  const billAmount = Number(row.billAmount);
  if (!Number.isFinite(billAmount)) return 0;
  return Math.abs(billAmount);
}

/**
 * Voucher total from line Amount sum; BillAmount header when lines repeat full bill
 * or when line sum is empty.
 */
export function resolveImportVoucherTotal(lineAmountSum, maxBillHeader) {
  const sum = Math.abs(Number(lineAmountSum) || 0);
  const bill = Math.abs(Number(maxBillHeader) || 0);
  if (sum <= 0) return bill;
  if (bill <= 0) return sum;
  if (sum > bill * 1.001) return bill;
  return sum;
}

/** Aggregate voucher totals from an in-memory row set (exact week file). */
export function aggregateVoucherTypeSalesFromRows(rows, selectedVoucherType = "all") {
  const bucket = new Map();
  const voucherTypeOptions = new Set();

  for (const row of rows) {
    const vt = normalizeVoucherType(row);
    voucherTypeOptions.add(vt);
    if (selectedVoucherType !== "all" && vt !== selectedVoucherType) continue;

    const prev = bucket.get(vt) || { voucherType: vt, records: 0, saleAmount: 0 };
    prev.records += 1;
    prev.saleAmount += lineSaleAmount(row);
    bucket.set(vt, prev);
  }

  const outRows = [...bucket.values()].sort((a, b) => b.saleAmount - a.saleAmount);
  const totals = outRows.reduce(
    (acc, r) => {
      acc.records += r.records;
      acc.saleAmount += r.saleAmount;
      return acc;
    },
    { records: 0, saleAmount: 0 }
  );

  return {
    rows: outRows,
    totals,
    scanned: rows.length,
    voucherTypeOptions: [...voucherTypeOptions].sort((a, b) => a.localeCompare(b)),
  };
}

/**
 * List uploaded week slices (from monthly docs + weeklyLog_*), newest first.
 */
export async function listUploadedWeekPeriods() {
  const snap = await getDocs(collection(db, COL_SALES_IMPORT_MONTHS));
  const weeks = [];
  snap.forEach((d) => {
    const data = d.data() || {};
    const year = Number(data.year);
    const month = Number(data.month);
    if (!year || !month) return;
    const monthIndex = month - 1;
    for (let w = 1; w <= SALES_WEEKS_PER_MONTH; w += 1) {
      const log = data[`weeklyLog_${w}`];
      if (!log || !log.rowCount) continue;
      const meta = buildMonthWeekMeta(year, monthIndex, w);
      weeks.push({
        periodKey: log.periodKey || meta.periodKey,
        fromDate: log.fromDate || meta.fromDate,
        toDate: log.toDate || meta.toDate,
        year,
        monthIndex,
        weekOfMonth: w,
        rowCount: Number(log.rowCount) || 0,
        uploadedAt: log.uploadedAt || "",
        fileName: log.fileName || "",
        monthId: d.id,
      });
    }
  });
  weeks.sort((a, b) => String(b.uploadedAt).localeCompare(String(a.uploadedAt)));
  return weeks;
}

export async function countWeeklyLineChunkRows(year, monthIndex, weekOfMonth) {
  const monthId = salesImportMonthDocId(year, monthIndex);
  const snap = await getDoc(doc(db, COL_SALES_IMPORT_MONTHS, monthId));
  if (!snap.exists()) return 0;
  const log = snap.data()?.[`weeklyLog_${weekOfMonth}`];
  return Number(log?.rowCount) || 0;
}

export async function countAllLineChunkRows() {
  const weeks = await listUploadedWeekPeriods();
  return weeks.reduce((s, w) => s + (w.rowCount || 0), 0);
}

/** Load all rows for one calendar week slice (~30k rows = few chunk docs). */
export async function loadWeeklyLineChunkRows(year, monthIndex, weekOfMonth, { fromDate, toDate } = {}) {
  const weekMeta = buildMonthWeekMeta(year, monthIndex, weekOfMonth);
  const rangeFrom = fromDate || weekMeta.fromDate;
  const rangeTo = toDate || weekMeta.toDate;
  const monthId = salesImportMonthDocId(year, monthIndex);
  const category = salesTransactionsWeekCategory(weekOfMonth);
  const colRef = collection(db, COL_SALES_IMPORT_MONTHS, monthId, SALES_IMPORT_LINE_CHUNKS_SUB);
  const snap = await getDocs(colRef);
  const rows = [];
  const chunks = snap.docs
    .filter((d) => (d.data()?.category || "") === category)
    .sort((a, b) => Number(a.data()?.index ?? 0) - Number(b.data()?.index ?? 0));
  for (const d of chunks) {
    const part = d.data()?.rows;
    if (Array.isArray(part)) {
      for (const row of part) {
        if (rangeFrom || rangeTo) {
          if (!rowMatchesDateRange(row, rangeFrom, rangeTo)) continue;
        }
        const tranDate = normalizeTranDateString(
          row.tranDate ?? row["Trans Date"] ?? row.TransDate ?? ""
        );
        const branchId = normalizeSalesImportBranchId(
          row.branchId ?? row.BranchID ?? row.branch ?? ""
        );
        rows.push({
          ...row,
          ...(tranDate ? { tranDate } : {}),
          ...(branchId ? { branchId } : {}),
          id: `${d.id}_${rows.length}`,
        });
      }
    }
  }
  return rows;
}

function pushChunkRow(rows, row, fromDate, toDate, rowFilter) {
  if (!rowMatchesDateRange(row, fromDate, toDate)) return;
  if (rowFilter && !rowFilter(row)) return;
  const branchId = normalizeSalesImportBranchId(row.branchId ?? row.BranchID ?? row.branch ?? "");
  rows.push(branchId ? { ...row, branchId } : row);
}

async function loadWeekCategoryChunks(colRef, category, fromDate, toDate, rowFilter) {
  const snap = await getDocs(query(colRef, where("category", "==", category)));
  const rows = [];
  const chunks = snap.docs.sort(
    (a, b) => Number(a.data()?.index ?? 0) - Number(b.data()?.index ?? 0)
  );
  for (const d of chunks) {
    const part = d.data()?.rows;
    if (!Array.isArray(part)) continue;
    for (const row of part) {
      pushChunkRow(rows, row, fromDate, toDate, rowFilter);
    }
  }
  return rows;
}

async function loadMonthLineChunkRows(year, monthIndex, fromDate, toDate, rowFilter, onProgress) {
  const monthId = salesImportMonthDocId(year, monthIndex);
  const monthSnap = await getDoc(doc(db, COL_SALES_IMPORT_MONTHS, monthId));
  if (!monthSnap.exists()) return [];

  const monthData = monthSnap.data() || {};
  const colRef = collection(db, COL_SALES_IMPORT_MONTHS, monthId, SALES_IMPORT_LINE_CHUNKS_SUB);
  const weekJobs = [];

  for (let w = 1; w <= SALES_WEEKS_PER_MONTH; w += 1) {
    const log = monthData[`weeklyLog_${w}`];
    if (!log?.rowCount) continue;

    const weekMeta = buildMonthWeekMeta(year, monthIndex, w);
    const weekFrom = log.fromDate || weekMeta.fromDate;
    const weekTo = log.toDate || weekMeta.toDate;
    if (!weekRangeOverlapsFilter(weekFrom, weekTo, fromDate, toDate)) continue;

    weekJobs.push(
      loadWeekCategoryChunks(
        colRef,
        salesTransactionsWeekCategory(w),
        fromDate,
        toDate,
        rowFilter
      ).then((rows) => {
        onProgress?.(rows.length, 1);
        return rows;
      })
    );
  }

  if (!weekJobs.length) return [];
  const parts = await Promise.all(weekJobs);
  return parts.flat();
}

/**
 * Load rows for a date range by reading only relevant week lineChunks (fast vs 30k+ doc scan).
 */
export async function loadLineChunkRowsForDateRange(
  { fromDate = "", toDate = "" } = {},
  onProgress,
  { rowFilter = null } = {}
) {
  const months = monthsOverlappingRange(fromDate, toDate);
  let chunksLoaded = 0;

  const monthParts = await Promise.all(
    months.map(({ year, monthIndex }) =>
      loadMonthLineChunkRows(year, monthIndex, fromDate, toDate, rowFilter, (rowCount, chunkInc) => {
        chunksLoaded += chunkInc || 0;
        onProgress?.(rowCount, chunksLoaded);
      })
    )
  );

  const allRows = monthParts.flat();
  return { rows: allRows, chunksLoaded };
}

export async function aggregateVoucherTypeSalesFromLineChunks({
  fromDate = "",
  toDate = "",
  selectedVoucherType = "all",
  onProgress,
}) {
  const { rows } = await loadLineChunkRowsForDateRange({ fromDate, toDate }, (rowCount) => {
    onProgress?.(rowCount);
  });
  return aggregateVoucherTypeSalesFromRows(rows, selectedVoucherType);
}

export async function deleteAllSalesImportLineChunks(onProgress) {
  const snap = await getDocs(collection(db, COL_SALES_IMPORT_MONTHS));
  let deletedChunks = 0;
  for (const monthDoc of snap.docs) {
    const colRef = collection(db, COL_SALES_IMPORT_MONTHS, monthDoc.id, SALES_IMPORT_LINE_CHUNKS_SUB);
    const chunks = await getDocs(colRef);
    for (let i = 0; i < chunks.docs.length; i += BATCH_WRITE_SIZE) {
      const batch = writeBatch(db);
      for (const c of chunks.docs.slice(i, i + BATCH_WRITE_SIZE)) {
        batch.delete(c.ref);
        deletedChunks += 1;
      }
      await batch.commit();
    }
    await deleteDoc(monthDoc.ref);
    onProgress?.(deletedChunks);
  }
  return deletedChunks;
}
